/** Badge identification: exactly 16 hexadecimal characters, prefixed with `01` (01D92A14010000B7). */
const IDENTIFICATION_LENGTH = 16;
const IDENTIFICATION_PREFIX = '01';
/** Internal: callers go through the predicates below, which normalize the case first. */
const IDENTIFICATION_PATTERN = new RegExp(
  `^${IDENTIFICATION_PREFIX}[0-9A-F]{${IDENTIFICATION_LENGTH - IDENTIFICATION_PREFIX.length}}$`
);

/** PND identification: exactly 4 digits (0042), the code typed on the device itself. */
const PND_IDENTIFICATION_LENGTH = 4;
const PND_IDENTIFICATION_PATTERN = new RegExp(`^[0-9]{${PND_IDENTIFICATION_LENGTH}}$`);

/** How the identification field must be filled, stated once for the validators and the UI. */
interface IdentificationFormat {
  /** Expected number of characters, which is also the maximum the field accepts. */
  readonly length: number;
  /** Translation key for the error, stating the whole rule. */
  readonly message: string;
  /** Translation key for the hint shown while the field has focus. */
  readonly hint: string;
}

const BADGE_FORMAT: IdentificationFormat = {
  length: IDENTIFICATION_LENGTH,
  message: 'DRIVER.IDENTIFICATION_FORMAT',
  hint: 'DRIVER.IDENTIFICATION_HINT',
};

const PND_FORMAT: IdentificationFormat = {
  length: PND_IDENTIFICATION_LENGTH,
  message: 'DRIVER.IDENTIFICATION_FORMAT_PND',
  hint: 'DRIVER.IDENTIFICATION_HINT_PND',
};

/**
 * The format a driver's identification must follow. A PND driver is identified by the short code
 * typed on the device, the others by the number of the badge they carry — one field, two rules,
 * selected by the PND flag.
 *
 * Returns a shared constant rather than a new object: this is read from a template binding.
 */
export function identificationFormat(isPnd: boolean): IdentificationFormat {
  return isPnd ? PND_FORMAT : BADGE_FORMAT;
}

/**
 * Uppercases an identification, the badge format using uppercase hexadecimal letters. Typing and
 * pasting are normalized through this function, so a stored identification never holds lowercase.
 * A PND code holds no letter, so this leaves it untouched.
 */
export function normalizeIdentification(identification: string): string {
  return identification ? identification.toUpperCase() : identification;
}

/**
 * Whether a driver actually carries a usable identification. Case is normalized first, the form
 * storing uppercase but legacy values possibly holding lowercase for the very same badge. Anything
 * else off-format — empty, wrong length, not hexadecimal, missing the `01` prefix, or not the four
 * digits a PND driver is identified by — is reported as no badge at all, so those values surface in
 * the "without badge" filter instead of passing as valid.
 */
export function hasBadgeIdentification(identification: string, isPnd: boolean): boolean {
  const pattern = isPnd ? PND_IDENTIFICATION_PATTERN : IDENTIFICATION_PATTERN;

  return !!identification && pattern.test(normalizeIdentification(identification));
}

/**
 * Whether an identification is an acceptable value for the driver form. Same format rule as
 * `hasBadgeIdentification`, with the opposite answer on an empty value: the field is optional, so
 * entering no identification at all is valid even though it means the driver carries none.
 */
export function isValidIdentification(identification: string, isPnd: boolean): boolean {
  return !identification || hasBadgeIdentification(identification, isPnd);
}
